import { useEffect, useState, type FormEvent } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Copy, LoaderCircle, ShieldCheck, Trash2, Unplug } from "lucide-react"
import { toast } from "sonner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { api, type ChatwootIntegrationInput, type ChatwootIntegrationStatus, type GenesysSchedule } from "@/lib/api"
import { queryKeys } from "@/lib/query-keys"

const WEEKDAYS = [
  { day: 1, label: "Seg" },
  { day: 2, label: "Ter" },
  { day: 3, label: "Qua" },
  { day: 4, label: "Qui" },
  { day: 5, label: "Sex" },
  { day: 6, label: "Sáb" },
  { day: 0, label: "Dom" },
]

function minutesToTime(minutes: number) {
  const clamped = Math.min(1439, Math.max(0, minutes))
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`
}

function timeToMinutes(time: string) {
  const [hours, minutes] = time.split(":").map(Number)
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null
}

function sameSchedule(a: GenesysSchedule, b: GenesysSchedule) {
  return a.enabled === b.enabled && a.timezone === b.timezone && a.startMinute === b.startMinute && a.endMinute === b.endMinute
    && [...a.days].sort().join() === [...b.days].sort().join()
}

function handoffErrorCopy(code: string) {
  if (code.startsWith("crypto_")) return "As credenciais guardadas não puderam ser decifradas com a chave atual do servidor."
  if (code.startsWith("database_")) return "Erro no banco de dados ao registrar o encaminhamento. A tentativa pode ser repetida."
  switch (code) {
    case "authentication_failed": return "O Chatwoot recusou o token de acesso."
    case "provider_not_found": return "Conta, caixa de entrada ou conversa não encontrada no Chatwoot."
    case "inbox_not_api_channel": return "A caixa de entrada precisa ser do tipo API."
    case "unsafe_base_url": return "O endereço do Chatwoot não é permitido."
    case "provider_rejected_request": return "O Chatwoot recusou a requisição. Confira as permissões do token."
    case "provider_unavailable": return "O Chatwoot respondeu com erro interno. Vale tentar novamente."
    case "provider_rate_limited": return "O Chatwoot limitou as requisições (429)."
    case "provider_transport_failed": return "Não foi possível conectar ao Chatwoot (rede ou TLS)."
    case "invalid_provider_response": return "A resposta do Chatwoot veio em formato inesperado."
    case "invalid_provider_configuration": return "O endereço, a conta ou a caixa de entrada estão ausentes ou inválidos."
    case "encryption_unavailable": return "A chave de criptografia do servidor está indisponível."
    case "integration_disabled": return "A integração foi desativada antes do envio."
    case "conversation_ended": return "A conversa foi resolvida no Chatwoot antes do envio."
    default: return "Código não reconhecido; confira os registros da API."
  }
}

const TIMEZONE_OPTIONS: string[] = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : ["America/Sao_Paulo"]

const STEPS: Array<{ title: string; body: React.ReactNode }> = [
  {
    title: "Crie uma caixa de entrada do tipo API",
    body: <>No Chatwoot, vá em Configurações → Caixas de entrada → Adicionar caixa de entrada e escolha <strong>API</strong>. Anote o <strong>ID da caixa de entrada</strong> (aparece na URL da caixa) e o <strong>ID da conta</strong> (aparece na URL do painel).</>,
  },
  {
    title: "Gere o token de acesso",
    body: <>Em Perfil → Token de acesso, copie o token de um usuário com permissão de administrador ou agente na conta. O BubbleDesk usa esse token para criar contatos e conversas.</>,
  },
  {
    title: "Cadastre o webhook",
    body: <>Em Configurações → Integrações → Webhooks, adicione a URL abaixo e marque os eventos <code>message_created</code> e <code>conversation_status_changed</code>. Copie o segredo de assinatura exibido pelo Chatwoot.</>,
  },
  {
    title: "Preencha, salve e verifique",
    body: <>Informe os dados abaixo e clique em <strong>Salvar configuração</strong>. Depois use <strong>Verificar conexão</strong> para confirmar o token, a conta e a caixa de entrada, e então <strong>Ativar integração</strong>.</>,
  },
]

export function ChatwootIntegrationSection({ chatbotId }: { chatbotId: string }) {
  const queryClient = useQueryClient()
  const [schedule, setSchedule] = useState<GenesysSchedule | null>(null)
  const [baseUrl, setBaseUrl] = useState("")
  const [accountId, setAccountId] = useState("")
  const [inboxId, setInboxId] = useState("")
  const [apiAccessToken, setApiAccessToken] = useState("")
  const [webhookSecret, setWebhookSecret] = useState("")
  const statusQuery = useQuery({
    queryKey: queryKeys.chatwootIntegration(chatbotId),
    queryFn: () => api.getChatwootIntegration(chatbotId),
    enabled: Boolean(chatbotId),
  })
  const status = statusQuery.data

  useEffect(() => {
    setBaseUrl(status?.baseUrl ?? "")
    setAccountId(status?.accountId ?? "")
    setInboxId(status?.inboxId ?? "")
    setApiAccessToken("")
    setWebhookSecret("")
  }, [chatbotId, status?.baseUrl, status?.accountId, status?.inboxId])

  const serverScheduleKey = status ? JSON.stringify(status.schedule) : ""
  useEffect(() => {
    setSchedule(serverScheduleKey ? (JSON.parse(serverScheduleKey) as GenesysSchedule) : null)
  }, [chatbotId, serverScheduleKey])
  const currentSchedule = schedule ?? status?.schedule

  const store = (next: ChatwootIntegrationStatus) => queryClient.setQueryData(queryKeys.chatwootIntegration(chatbotId), next)
  const failure = (fallback: string) => (error: unknown) => toast.error(error instanceof Error ? error.message : fallback)
  const saveMutation = useMutation({
    mutationFn: (input: ChatwootIntegrationInput) => api.saveChatwootIntegration(chatbotId, input),
    onSuccess: (next) => {
      store(next)
      setApiAccessToken("")
      setWebhookSecret("")
      toast.success("Configuração Chatwoot salva com segurança.")
    },
    onError: failure("Não foi possível salvar a integração."),
  })
  const verifyMutation = useMutation({
    mutationFn: () => api.verifyChatwootIntegration(chatbotId),
    onSuccess: (next) => { store(next); toast.success("Conexão com o Chatwoot verificada.") },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.chatwootIntegration(chatbotId) })
      toast.error(error instanceof Error ? error.message : "Não foi possível verificar a conexão.")
    },
  })
  const statusMutation = useMutation({
    mutationFn: (next: "active" | "disabled") => api.setChatwootIntegrationStatus(chatbotId, next),
    onSuccess: (next) => { store(next); toast.success(next.status === "active" ? "Integração ativada." : "Integração desativada.") },
    onError: failure("Não foi possível alterar o status."),
  })
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteChatwootIntegration(chatbotId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.chatwootIntegration(chatbotId) })
      setApiAccessToken("")
      setWebhookSecret("")
      toast.success("Integração removida. O histórico local foi preservado.")
    },
    onError: failure("Não foi possível remover a integração."),
  })

  async function copyWebhookUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      toast.success("URL do webhook copiada.")
    } catch {
      toast.error("Não foi possível copiar. Selecione a URL e copie manualmente.")
    }
  }

  function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!status) return
    if (!baseUrl.trim()) return toast.error("Informe o endereço do Chatwoot.")
    if (!/^\d{1,12}$/.test(accountId.trim())) return toast.error("O ID da conta deve ser numérico.")
    if (!/^\d{1,12}$/.test(inboxId.trim())) return toast.error("O ID da caixa de entrada deve ser numérico.")
    if (currentSchedule?.enabled) {
      if (!currentSchedule.days.length) return toast.error("Selecione ao menos um dia de atendimento.")
      if (currentSchedule.endMinute <= currentSchedule.startMinute) return toast.error("O horário final deve ser maior que o inicial.")
      if (!currentSchedule.timezone.trim()) return toast.error("Informe o fuso horário.")
    }
    if ((!status.apiAccessTokenConfigured && !apiAccessToken.trim()) || (!status.webhookSecretConfigured && !webhookSecret.trim())) {
      return toast.error("Informe o token de acesso e o segredo do webhook para configurar.")
    }
    const input: ChatwootIntegrationInput = { baseUrl: baseUrl.trim(), accountId: accountId.trim(), inboxId: inboxId.trim() }
    if (currentSchedule) input.schedule = { ...currentSchedule, timezone: currentSchedule.timezone.trim(), days: [...currentSchedule.days].sort((a, b) => a - b) }
    if (apiAccessToken.trim()) input.apiAccessToken = apiAccessToken.trim()
    if (webhookSecret.trim()) input.webhookSecret = webhookSecret.trim()
    saveMutation.mutate(input)
  }

  if (statusQuery.isPending) return <div className="space-y-4" aria-busy="true"><Skeleton className="h-24 w-full rounded-2xl" /><Skeleton className="h-72 w-full rounded-2xl" /></div>
  if (statusQuery.error || !status) return <Alert variant="destructive"><Unplug className="size-4" /><AlertDescription>{statusQuery.error instanceof Error ? statusQuery.error.message : "Não foi possível carregar o status da integração."}</AlertDescription></Alert>

  const busy = saveMutation.isPending || verifyMutation.isPending || statusMutation.isPending || deleteMutation.isPending
  const disabled = !status.canManage || busy
  const hasUnsavedChanges = baseUrl !== (status.baseUrl ?? "") || accountId !== (status.accountId ?? "") || inboxId !== (status.inboxId ?? "")
    || Boolean(currentSchedule && !sameSchedule(currentSchedule, status.schedule))
    || Boolean(apiAccessToken || webhookSecret)
  const active = status.status === "active"

  return (
    <div className="space-y-5">
      <section className="panel-bg rounded-[22px] border border-border p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-[19px] font-bold">Atendimento humano com Chatwoot</h2>
            <p className="muted mt-1 max-w-2xl text-[12px] leading-relaxed">Encaminhe o atendimento para sua equipe no Chatwoot por uma caixa de entrada do tipo API. Apenas um provedor de atendimento humano fica ativo por assistente.</p>
          </div>
          <Badge className={`rounded-full border px-3 py-1 text-[11px] ${active ? "border-success/30 bg-success/10 text-success" : "border-border bg-muted/40 text-muted-foreground"}`}>{active ? "Ativa" : "Desativada"}</Badge>
        </div>
        {!active && <p role="status" className="muted mt-4 rounded-xl border border-border bg-background/40 p-3 text-[12px]">Com a integração desativada, o assistente virtual continua disponível. Se houver atendimento humano em andamento, o visitante recebe um aviso de indisponibilidade. As credenciais permanecem salvas.</p>}
        {status.lastError && <Alert variant="destructive" className="mt-4"><AlertDescription>Última verificação: {status.lastError.code} · {handoffErrorCopy(status.lastError.code)} · {status.lastError.at ? new Date(status.lastError.at).toLocaleString("pt-BR") : "horário indisponível"}</AlertDescription></Alert>}
        {status.lastHandoffError && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>
              Último encaminhamento falhou ({status.lastHandoffError.code}): {handoffErrorCopy(status.lastHandoffError.code)} · {new Date(status.lastHandoffError.at).toLocaleString("pt-BR")}
            </AlertDescription>
          </Alert>
        )}
      </section>

      <section aria-labelledby="chatwoot-setup-guide" className="panel-bg rounded-[22px] border border-border p-5 sm:p-6">
        <h3 id="chatwoot-setup-guide" className="text-[16px] font-semibold">Guia rápido de configuração</h3>
        <p className="muted mt-1 text-[13px] leading-relaxed">Cada assistente tem sua própria conexão. Use os dados da conta Chatwoot que vai atender este assistente.</p>
        <ol className="mt-6 grid gap-6 sm:grid-cols-2">
          {STEPS.map((step, index) => (
            <li key={step.title} className="flex gap-4">
              <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background/60 text-[13px] font-semibold">{index + 1}</span>
              <div className="space-y-1">
                <p className="text-[15px] font-medium">{step.title}</p>
                <p className="muted text-[14px] leading-relaxed">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-6 space-y-2">
          <Label htmlFor="chatwoot-webhook-url">URL do webhook</Label>
          {status.webhookUrl
            ? (
              <div className="flex items-center gap-2">
                <Input id="chatwoot-webhook-url" readOnly value={status.webhookUrl} className="font-mono text-[12px]" onFocus={(event) => event.currentTarget.select()} />
                <Button type="button" variant="outline" size="icon" aria-label="Copiar URL do webhook" onClick={() => void copyWebhookUrl(status.webhookUrl!)}><Copy className="size-4" /></Button>
              </div>
            )
            : <p className="rounded-lg border border-border bg-background/40 p-2 text-[12px]">A URL pública ainda não está disponível. O servidor precisa estar configurado com o endereço público HTTPS da aplicação.</p>}
        </div>
      </section>

      <form onSubmit={save} className="panel-bg space-y-5 rounded-[22px] border border-border p-5 sm:p-6">
        <div>
          <h3 className="text-[14px] font-semibold">Credenciais e integração</h3>
          <p className="muted mt-1 text-[11px]">Os segredos são enviados apenas neste formulário e ficam cifrados no servidor. Campos secretos aparecem vazios após salvar.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="chatwoot-base-url">URL do Chatwoot</Label>
            <Input id="chatwoot-base-url" type="url" inputMode="url" placeholder="https://chatwoot.suaempresa.com.br" value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} maxLength={500} autoComplete="off" disabled={disabled} required />
            <p className="muted text-[11px]">Precisa ser HTTPS e acessível publicamente pelo servidor do BubbleDesk.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="chatwoot-account-id">ID da conta</Label>
            <Input id="chatwoot-account-id" inputMode="numeric" value={accountId} onChange={(event) => setAccountId(event.target.value)} maxLength={12} autoComplete="off" disabled={disabled} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chatwoot-inbox-id">ID da caixa de entrada</Label>
            <Input id="chatwoot-inbox-id" inputMode="numeric" value={inboxId} onChange={(event) => setInboxId(event.target.value)} maxLength={12} autoComplete="off" disabled={disabled} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chatwoot-api-token">Token de acesso {status.apiAccessTokenConfigured && <span className="muted font-normal">(vazio mantém o salvo)</span>}</Label>
            <Input id="chatwoot-api-token" type="password" value={apiAccessToken} onChange={(event) => setApiAccessToken(event.target.value)} placeholder={status.apiAccessTokenConfigured ? "••••••••••••••••" : undefined} maxLength={2_000} autoComplete="new-password" spellCheck={false} disabled={disabled} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chatwoot-webhook-secret">Segredo do webhook {status.webhookSecretConfigured && <span className="muted font-normal">(vazio mantém o salvo)</span>}</Label>
            <Input id="chatwoot-webhook-secret" type="password" value={webhookSecret} onChange={(event) => setWebhookSecret(event.target.value)} placeholder={status.webhookSecretConfigured ? "••••••••••••••••" : undefined} maxLength={2_000} autoComplete="new-password" spellCheck={false} disabled={disabled} />
          </div>
        </div>
        {(status.apiAccessTokenConfigured || status.webhookSecretConfigured) && (
          <div className="grid gap-3 rounded-xl border border-border bg-background/40 p-3 text-[11px] sm:grid-cols-2">
            {status.apiAccessTokenConfigured && <p className="flex items-center gap-2"><ShieldCheck className="size-4 text-success" />Token de acesso configurado</p>}
            {status.webhookSecretConfigured && <p className="flex items-center gap-2"><ShieldCheck className="size-4 text-success" />Segredo do webhook configurado</p>}
          </div>
        )}
        {currentSchedule && (
          <fieldset className="space-y-4 rounded-xl border border-border bg-background/40 p-4" disabled={disabled}>
            <legend className="px-1 text-[14px] font-semibold">Horário de atendimento</legend>
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="chatwoot-schedule-enabled">Limitar o atendimento humano a um horário</Label>
                <p className="muted mt-1 text-[11px]">Fora do horário, o visitante recebe um aviso e o assistente continua respondendo. Desativado, o atendimento humano fica disponível o tempo todo.</p>
              </div>
              <Switch id="chatwoot-schedule-enabled" checked={currentSchedule.enabled} onCheckedChange={(enabled) => setSchedule({ ...currentSchedule, enabled })} />
            </div>
            <div className={currentSchedule.enabled ? "space-y-4" : "pointer-events-none space-y-4 opacity-50"} aria-disabled={!currentSchedule.enabled}>
              <div className="space-y-2">
                <span className="text-[13px] font-medium">Dias de atendimento</span>
                <div className="flex flex-wrap gap-3">
                  {WEEKDAYS.map(({ day, label }) => (
                    <label key={day} className="flex items-center gap-2 text-[13px]">
                      <Checkbox
                        checked={currentSchedule.days.includes(day)}
                        disabled={!currentSchedule.enabled}
                        onCheckedChange={(checked) => setSchedule({
                          ...currentSchedule,
                          days: checked ? [...currentSchedule.days, day] : currentSchedule.days.filter((item) => item !== day),
                        })}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="chatwoot-schedule-start">Início</Label>
                  <Input id="chatwoot-schedule-start" type="time" value={minutesToTime(currentSchedule.startMinute)} disabled={!currentSchedule.enabled} onChange={(event) => { const minutes = timeToMinutes(event.target.value); if (minutes !== null) setSchedule({ ...currentSchedule, startMinute: minutes }) }} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chatwoot-schedule-end">Fim</Label>
                  <Input id="chatwoot-schedule-end" type="time" value={minutesToTime(currentSchedule.endMinute)} disabled={!currentSchedule.enabled} onChange={(event) => { const minutes = timeToMinutes(event.target.value); if (minutes !== null) setSchedule({ ...currentSchedule, endMinute: minutes }) }} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chatwoot-schedule-timezone">Fuso horário</Label>
                  <Input id="chatwoot-schedule-timezone" list="chatwoot-timezones" value={currentSchedule.timezone} maxLength={64} autoComplete="off" disabled={!currentSchedule.enabled} onChange={(event) => setSchedule({ ...currentSchedule, timezone: event.target.value })} />
                  <datalist id="chatwoot-timezones">{TIMEZONE_OPTIONS.map((zone) => <option key={zone} value={zone} />)}</datalist>
                </div>
              </div>
              {currentSchedule.endMinute <= currentSchedule.startMinute && <p role="alert" className="text-[11px] text-destructive">O horário final deve ser maior que o inicial.</p>}
            </div>
          </fieldset>
        )}
        {status.configured && !status.lastVerifiedAt && <p role="status" className="muted rounded-xl border border-border p-3 text-[11px]">Verifique a conexão depois de salvar. A ativação só fica disponível após essa verificação.</p>}
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
          {status.canManage && <Button type="submit" disabled={busy || !baseUrl.trim() || !accountId.trim() || !inboxId.trim()}>{saveMutation.isPending ? <LoaderCircle className="mr-2 size-4 animate-spin" /> : null}Salvar configuração</Button>}
          {status.canManage && status.configured && <Button type="button" variant="outline" disabled={busy || hasUnsavedChanges} onClick={() => verifyMutation.mutate()}>{verifyMutation.isPending ? <LoaderCircle className="mr-2 size-4 animate-spin" /> : null}Verificar conexão</Button>}
          {status.canManage && status.configured && !active && (
            <Button type="button" variant="outline" disabled={busy || hasUnsavedChanges || !status.lastVerifiedAt || !status.webhookUrl} onClick={() => statusMutation.mutate("active")}>
              {statusMutation.isPending ? <LoaderCircle className="mr-2 size-4 animate-spin" /> : null}
              {status.lastVerifiedAt ? "Ativar integração" : "Verificar conexão para ativar"}
            </Button>
          )}
          {status.canManage && active && (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button type="button" variant="outline" disabled={busy}>Desativar</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Desativar integração Chatwoot?</AlertDialogTitle>
                  <AlertDialogDescription>Atendimentos humanos em andamento serão encerrados no BubbleDesk e o visitante receberá um aviso de indisponibilidade. As credenciais continuarão salvas.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => statusMutation.mutate("disabled")}>Desativar integração</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {status.canManage && status.configured && (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button type="button" variant="destructive" disabled={busy} className="sm:ml-auto"><Trash2 className="mr-2 size-4" />Remover integração</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remover conexão Chatwoot?</AlertDialogTitle>
                  <AlertDialogDescription>As credenciais, vínculos e itens pendentes da integração serão removidos. As conversas e mensagens locais continuam no histórico.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => deleteMutation.mutate()}>Remover</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        {!status.canManage && <p role="status" className="muted border-t border-border pt-4 text-[11px]">Você tem acesso de leitura. Somente o dono deste assistente ou um superadmin pode alterar a integração.</p>}
      </form>
    </div>
  )
}
